import React, { createContext, useEffect, useState, useRef,} from "react";
import Matter from "matter-js";
import { gsap } from "gsap";
export const MatterContext = createContext();

export const MatterProvider = ({ children, canvasTargetRef, disableWalls = false}) => {
  const [worldReady, setWorldReady] = useState(false);
  const canvasRef = useRef();
  const [engine, setEngine] = useState(null);
  const [world, setWorld] = useState(null);
  const [iceBodies, setIceBodies] = useState([]);
  const [sweetBodies, setSweetBodies] = useState([]);
  const [strongBodies, setStrongBodies] = useState([]);
  const [canvasVisible, setCanvasVisible] = useState(true);
  const [CanvasFade, setCanvasFade] = useState(false);
  const [blenderVisible, setBlenderVisible] = useState(false);
  const [blenderFade, setBlenderFade] = useState(false);
  const [liquidLayers, setLiquidLayers] = useState([]);   // [{style, state, removeAt?}]
  const [liquidGifKeys, setLiquidGifKeys] = useState({}); // { [style]: number }
  const [blenderBox, setBlenderBox] = useState(null);     // {left, top, width, height, offsetY?}

  const topWallRef = useRef(null);
  const baseTopWallPos = useRef(null);
  const blurCacheRef = useRef(new Map());
  const DEBUG = false;
  const DEFAULT_CANVAS_W = 350;
  const DEFAULT_CANVAS_H = 590;
  const [canvasPosition, setCanvasPosition] = useState({
    width: `${DEFAULT_CANVAS_W}px`,
    height: `${DEFAULT_CANVAS_H}px`,
  });

  const updateMatterPosition = (anchorEl) => {
    if (DEBUG) console.log("[updateMatterPosition]", { hasAnchor: !!anchorEl });

    const canvas = canvasRef?.current;
    const blender = canvasTargetRef?.current;
  
    if (!canvas || !blender || !anchorEl) {
      return;
    }

    if (DEBUG && (!canvas || !blender || !anchorEl)) {
      console.log("[updateMatterPosition] EARLY RETURN (missing refs)", {
        hasCanvas: !!canvas,
        hasBlender: !!blender,
        hasAnchor: !!anchorEl,
        anchorEl,
        blenderEl: blender,
        canvasEl: canvas,
      });
      return;
    }
  
    // 🧃 먼저 blender를 anchor 박스 기준으로 이동
    const anchorRect = anchorEl.getBoundingClientRect();
    if (DEBUG) console.log("[MatterProvider] anchorRect:", anchorRect);
    const blenderOffsetX = 40;
    const blenderOffsetY = 0;
  
    const blenderLeft = `${anchorRect.left + blenderOffsetX}px`;
    const blenderTop = `${anchorRect.top + blenderOffsetY}px`;
  
    Object.assign(blender.style, { left: blenderLeft, top: blenderTop, bottom: "auto" });
  
    // 🧊 그리고 나서 canvas를 blender 기준으로 이동
    const blenderRect = blender.getBoundingClientRect();
    const canvasOffsetX = -25;
    const canvasOffsetY = -245;
  
    const canvasLeft = `${blenderRect.left + canvasOffsetX}px`;
    const canvasTop = `${blenderRect.top + canvasOffsetY}px`;

    if (DEBUG) console.log("[updateMatterPosition] canvas.style(before) =", canvasLeft, canvasTop);
    
    Object.assign(canvas.style, { left: canvasLeft, top: canvasTop });
    setCanvasPosition((prev) => ({ ...prev, left: canvasLeft, top: canvasTop }));

    // WebM 레이어가 이 박스를 기준으로 클리핑/레이어링을 계산함
    setBlenderBox({
      left: blenderRect.left,
      top: blenderRect.top,
      width: blenderRect.width,
      height: blenderRect.height,
      offsetY: 190, // 현재 BlenderLiquidLayers에서 쓰는 보정치
    });

    if (DEBUG) console.log("[updateMatterPosition] canvas.style(after)  =", canvasLeft, canvasTop);

  };

  const fadeOutAndClearBodies = (durationMs = 350) => {
    return new Promise((resolve) => {
      const targets = [...iceBodies, ...sweetBodies, ...strongBodies];
      if (!targets.length) { resolve(); return; }
      let done = 0;
        const onOneDone = () => { if (++done === targets.length) resolve(); };
        targets.forEach((body) => {
          // sprite 스케일 0으로 축소 → onComplete에서 제거
          const spr = body?.render?.sprite;
          if (spr) {
            gsap.to(spr, {
              xScale: 0, yScale: 0,
              duration: durationMs / 1000,
              ease: "power1.inOut",
              onComplete: () => { removeBody(body); onOneDone(); }
            });
          } else {
            removeBody(body);
            onOneDone();
          }
        });
      // 상태 배열도 정리
      setIceBodies([]); setSweetBodies([]); setStrongBodies([]);
    });
  };

  // ✅ Recommend 진입/이탈용: 바디를 "삭제"가 아니라 "보관"했다가 복원하기
  const stashedBodiesRef = useRef({ ice: [], sweet: [], strong: [] });

  const fadeOutAndStashBodies = (durationMs = 350) => {
    return new Promise((resolve) => {
      // 1) 현재 바디들 보관
      const stash = {
        ice: [...iceBodies],
        sweet: [...sweetBodies],
        strong: [...strongBodies],
      };
      stashedBodiesRef.current = stash;

      // 2) 실제 월드에서 제거(보이기만 없애고, 나중에 복원할 수 있게 객체는 보관)
      const targets = [...stash.ice, ...stash.sweet, ...stash.strong].filter(Boolean);
      if (!targets.length) { resolve(); return; }

      let done = 0;
      const onOneDone = () => { if (++done === targets.length) resolve(); };

      targets.forEach((body) => {
        const spr = body?.render?.sprite;

        // 원래 스케일 저장(복원용)
        if (spr) {
          body.plugin = body.plugin || {};
          if (!body.plugin.__origSpriteScale) {
            body.plugin.__origSpriteScale = {
              x: (spr.xScale ?? 1),
              y: (spr.yScale ?? 1),
            };
          }

          gsap.to(spr, {
            xScale: 0,
            yScale: 0,
            duration: durationMs / 1000,
            ease: "power1.inOut",
            onComplete: () => { removeBody(body); onOneDone(); }
          });
        } else {
          removeBody(body);
          onOneDone();
        }
      });

      // 3) 상태 배열은 비워서 UI/로직도 "없다"로 인식시키기
      setIceBodies([]);
      setSweetBodies([]);
      setStrongBodies([]);
    });
  };

  const restoreStashedBodies = () => {
    const stash = stashedBodiesRef.current || { ice: [], sweet: [], strong: [] };
  
    const total =
      (stash.ice?.length || 0) +
      (stash.sweet?.length || 0) +
      (stash.strong?.length || 0);
  
    // ✅ stash가 비어있으면: 아무 것도 복원하지 말고, 기존 iceBodies 상태도 건드리지 마
    if (total === 0) return;
  
    const restoreOne = (arr, setter) => {
      const restored = [];
      (arr || []).forEach((body) => {
        if (!body) return;
  
        const spr = body?.render?.sprite;
        const orig = body?.plugin?.__origSpriteScale;
        if (spr && orig) {
          spr.xScale = orig.x;
          spr.yScale = orig.y;
        }
  
        addBody(body);
        restored.push(body);
      });
      setter(restored);
    };
  
    restoreOne(stash.ice, setIceBodies);
    restoreOne(stash.sweet, setSweetBodies);
    restoreOne(stash.strong, setStrongBodies);
  
    stashedBodiesRef.current = { ice: [], sweet: [], strong: [] };
  };
  

  const clearStashedBodies = () => {
    stashedBodiesRef.current = { ice: [], sweet: [], strong: [] };
  };
  

  useEffect(() => {
    if (disableWalls && world) {
      const walls = world.bodies.filter((b) =>
        ["Left Wall", "Right Wall", "Bottom"].includes(b.label)
      );
      walls.forEach((w) => Matter.World.remove(world, w));
    }
  }, [disableWalls, world]);


  useEffect(() => {
    let raf1 = 0, raf2 = 0;
    const waitForCanvas = (cb) => {
      const el = canvasRef?.current;
      if (el) return cb(el);
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => waitForCanvas(cb));
      });
    };

    return waitForCanvas(() => {
      const _engine = Matter.Engine.create();
      const runner = Matter.Runner.create();
      const _world = _engine.world;
      _engine.gravity.y = 1; // 중력 기본값

      setEngine(_engine);
      setWorld(_world);
    
    // 2. 디버깅용 렌더러 생성 (canvas로 확인 가능)

      const renderWidth = DEFAULT_CANVAS_W;
      const renderHeight = DEFAULT_CANVAS_H;
      const _render = Matter.Render.create({

      canvas : canvasRef.current,
      engine: _engine,
      options: {
        width: renderWidth,
        height: renderHeight,
        background: "transparent",
        wireframes: false,
        showSprite: false,
        showBounds: false,
        showAxes: false,
        showPositions: false,
      },
    });

    Matter.Render.run(_render);
    Matter.Runner.run(runner, _engine);

    setWorldReady(true);

    Object.assign(_render.canvas.style, {
      position: "fixed",             // 
      width: `${renderWidth}px`,                // ✅ 사이즈도 확실히 지정
      height: `${renderHeight}px`,
      zIndex: "70",                // ✅ 무조건 위로
      pointerEvents: "none",
      // backgroundColor: "rgba(255,0,0,0.5)", // ✅ 반투명 빨간색으로 아주 잘 보이게!
      // border: "10px solid lime",      // ✅ 눈에 띄는 초록 테두리
    });    

      // 정리
      return () => {
        cancelAnimationFrame(raf1); cancelAnimationFrame(raf2);
        Matter.Render.stop(_render);
        Matter.Engine.clear(_engine);
        _render.canvas.remove();
        _render.textures = {};
      };
    });
  }, []); // ✅ 의존성은 빈 배열 (한 번만), 내부에서 캔버스 준비를 기다림

  useEffect(() => {
    if (!world || disableWalls !== false) return;
  
    const alreadyExists = world.bodies.some(
      (b) =>
        b.label === "Left Wall" ||
        b.label === "Right Wall" ||
        b.label === "Bottom"
    );
  
    if (alreadyExists) return; 

    if (canvasRef.current) canvasRef.current.style.zIndex = "70";
    if (DEBUG) console.log("✅ 벽 생성 시작!");
  
    // 🧊 Matter 렌더링 영역 (캔버스 기준)
    const canvasWidth = 350;
    const canvasHeight = 590;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const offsetY = 100;
    const offsetX = 105; //벽간격

    // 🧱 바구니 구성 요소 크기
    const basketWidth = 150;        // 바닥 가로폭
    const wallThickness = 10;       // 벽 두께
    const wallHeight = 400;          // 좌우 벽 높이

    // 📐 바구니 위치 기준
    const basketBottomY = canvasHeight;      // 바닥 중심 Y 좌표

    // 🌀 각도 (라디안)
    const wallAngle = Math.PI / 15;  // 약 15도 (0.26 라디안)

    
    // 왼쪽 벽 (안쪽으로 기울이기)
    const leftWall = Matter.Bodies.rectangle(
      centerX - offsetX,
      centerY + offsetY,
      wallThickness,
      wallHeight,
      {
        isStatic: true,
        angle: -wallAngle, // 반시계 방향 = 안쪽으로 기울임
        label: "Left Wall",
        render:{
          fillStyle : "transparent"
          // // fillStyle: "lime",
          // // strokeStyle: "black",
          // // lineWidth: 2,
          // visible: true,
        }
      }
    );

    const rightWall = Matter.Bodies.rectangle(
      centerX + offsetX,
      centerY + offsetY,
      wallThickness,
      wallHeight,
      {
        isStatic: true,
        angle: wallAngle, // 시계 방향 = 안쪽으로 기울임
        label: "Right Wall",
        render:{
          fillStyle : "transparent"
          // fillStyle: "lime",
          // strokeStyle: "black",
          // lineWidth: 2,
          // visible: true,
          
        }
      }
    );

    // 바닥
    const bottom = Matter.Bodies.rectangle(
      centerX,
      basketBottomY,
      basketWidth,
      wallThickness,
      {
        isStatic: true,
        label: "Bottom",
        render:{
          fillStyle : "transparent"
          // fillStyle: "lime",
          // strokeStyle: "black",
          // lineWidth: 2,
          // visible: true,
        }
      }
    );
  
    Matter.World.add(world, [leftWall, rightWall, bottom]);
  }, [disableWalls, world]);


// 3. 바디를 월드에 추가
const addBody = (body) => {
  if (world) Matter.World.add(world, body);
};

// 4. 바디를 월드에서 제거
const removeBody = (body) => {
  if (world) Matter.World.remove(world, body);
};

const createFullscreenCanvas = () => {
  if (!canvasRef.current) return false;

  setCanvasVisible(true);

  const canvas = canvasRef.current;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  Object.assign(canvas.style, {
    position: "fixed",
    width: "100vw", 
    height: "100vh",
    position: "fixed",
    left: "0",
    top: "0",
    zIndex: 70,
    opacity: 1,
    visibility: "visible",
    willChange: "transform",
    transform: "translateZ(0)",
    // backgroundColor: "rgba(0,255,0,0.05)", // 디버그용
    // border: "1px dashed lime",
    pointerEvents: "none", // 마우스 막기
  });

  setCanvasPosition({
    left: "0px",
    top: "0px",
    width: "100vw",
    height: "100vh",
  });
  
  return true;
};

// RecommendPage에서 뚜껑 끼우기용 wall 생성
const createTopWalls = (blenderRect) => {
  if (!world || !canvasRef.current || !blenderRect) return false;

  // 1) 뷰포트 → 캔버스 좌표 변환
  const canvasBounds = canvasRef.current.getBoundingClientRect();
  const centerX = (blenderRect.left + blenderRect.width / 2) - canvasBounds.left;
  const lipOffsetY = 70;
  const topY = (blenderRect.top /* 뚜껑 위치 기준 */) - canvasBounds.top + lipOffsetY;
  // console.log("[TW] topY=", topY, " (= br.top", blenderRect.top, "+", lipOffsetY, "- cv.top", canvasBounds.top, ")");


  // 2) 블렌더 폭에 비례한 너비(하드코딩 대신)
  const wallWidth = Math.min(blenderRect.width * 0.6, 220);
  const wallThickness = 10;

  // 3) 기존 Top* 벽 제거 (중복 생성 방지)
  const toRemove = world.bodies.filter(b =>
    b.label === "TopWall" || b.label === "TopLeftWall" || b.label === "TopRightWall"
  );
  toRemove.forEach(b => Matter.World.remove(world, b));

  // 4) 새 TopWall 생성
  const topWall = Matter.Bodies.rectangle(centerX, topY, wallWidth, wallThickness, {
    isStatic: true,
    label: "TopWall",
    render: { fillStyle: "transparent" }, // 오타(tranparent)도 수정
    // render: { fillStyle: "lime" },
  });

  topWallRef.current = topWall;
  baseTopWallPos.current = { x: topWall.position.x, y: topWall.position.y };

  Matter.World.add(world, [topWall]);
  return true;
};

const removeWallByLabel = (label) => {
  if (!world) return;
  const target = world.bodies.find((b) => b.label === label);
  if (target) Matter.World.remove(world, target);
};

// 리팩터링된 version: 규칙적인 진동
let shakeStartTime = Date.now();

const shakeBodiesByLabels = (labels, maxAngle = 0.05, freq = 6) => {
  if (!world) return;

  const now = Date.now();
  const t = (now - shakeStartTime) / 1000; // 초 단위 경과 시간
  const angle = Math.sin(t * freq) * maxAngle;

  labels.forEach((label) => {
    const body = world.bodies.find((b) => b.label === label);
    if (body) {
      Matter.Body.setAngle(body, angle); // ✅ 절대값으로 적용
    }
  });
};

// src(파일경로 or id) + blurPx → /explosion/{id}@b{px}.png 를 로드
const makeBlurredImage = (src, blurPx = 0) => new Promise((resolve, reject) => {
  const key = `${src}|${blurPx}`;
  const cache = blurCacheRef.current;
  if (cache.has(key)) return resolve(cache.get(key));

  // "경로/이름.png?hash" → "이름" 으로 변환해 id화
  const toId = (s) => {
    const m = String(s || "").match(/([^\/\\]+?)(?:\.[a-z0-9]+)?(?:\?.*)?$/i);
    return m ? m[1] : s;
  };
  const id = toId(src);
  const url = `/explosion/${id}@b${blurPx}.png`;

  const img = new Image();
  img.onload = () => { cache.set(key, img); resolve(img); };
  img.onerror = () => {
    // 블러 파일이 없으면 원본으로 폴백(로그만 찍고 계속)
    // console.warn('[makeBlurredImage] not found:', url, '→ fallback:', src);
    const orig = new Image();
    orig.onload = () => { cache.set(key, orig); resolve(orig); };
    orig.onerror = reject;
    orig.src = src;
  };
  img.src = url;
});

const getPreblurredSrc = async (src, blurPx) => {
  const img = await makeBlurredImage(src, blurPx);
  return img.src;
};
const chooseBlurPxByScale = (scale) => {
  if (scale <= 0.5) return 0;
  if (scale <= 0.6) return 2;
  return 4;
};
const getPreblurredByScale = async (src, scale) => {
  const px = chooseBlurPxByScale(scale);
  return getPreblurredSrc(src, px);
};
const preloadBlurVariants = async (srcList) => {
  const levels = [0, 2, 4]; // 빌드 스크립트 레벨과 일치
  await Promise.allSettled(srcList.flatMap(s => levels.map(px => makeBlurredImage(s, px))));
};


  return (
    <MatterContext.Provider value={{ 
      engine, 
      world, 
      addBody, 
      removeBody, 
      iceBodies, 
      setIceBodies,
      sweetBodies,
      setSweetBodies,
      strongBodies,
      setStrongBodies,
      setCanvasVisible,
      blenderRef : canvasTargetRef,
      setCanvasFade, 
      blenderVisible,
      setBlenderVisible,
      blenderFade,
      setBlenderFade,
      updateMatterPosition,
      createTopWalls,
      removeWallByLabel,
      createFullscreenCanvas,
      // resetCanvasToDefault,
      canvasRef,
      shakeBodiesByLabels,
      fadeOutAndClearBodies,
      fadeOutAndStashBodies,
      restoreStashedBodies,
      clearStashedBodies,
      topWallRef,
      baseTopWallPos,
      liquidLayers, setLiquidLayers,
      liquidGifKeys, setLiquidGifKeys,
      blenderBox, setBlenderBox,
      worldReady, makeBlurredImage,
      getPreblurredSrc, preloadBlurVariants,
      chooseBlurPxByScale, getPreblurredByScale,
    }}>
      <canvas
        ref={(el) => {canvasRef.current = el; }}
        style={{
          opacity : canvasVisible ? 1 : 0,
          position: "fixed",
          left: canvasPosition.left,
          top: canvasPosition.top,
          width: canvasPosition.width,
          height: canvasPosition.height,
          zIndex: 70, 
          transition: "opacity 0.7s ease-in-out",
          pointerEvents: "none",
        }}
      />     
      {children}
    </MatterContext.Provider>
  );
  
};
